/* global RequestInit */
import { BaseUiSchemaKeys } from '@core/shared/features/PostResultForm/types';
import { Cohesion } from '@core/ts/cohesion';

import Lead, { Location } from '../ts/leadDelivery';
import { PageActions, newRelicCustomAttribute, newRelicNoticeError, nrErrorMap } from './newRelic';

export type ContactRequest = {
  degree: string;
  subject: string;
  category: string;
  firstName: Lead['firstName'];
  lastName: Lead['lastName'];
  email: Lead['email'];
  phoneNumber: Lead['phoneNumber'];
  zip: Location['postalCode'];
  details: Lead;
  cohesionSessionId: string;
  trackingContext?: Cohesion['webContext'] & {
    publisher: string;
  };
};

type ProspectContact = {
  contactId: string;
  firstName: Lead['firstName'];
  lastName: Lead['lastName'];
  email: Lead['email'];
  phoneNumber: Lead['phoneNumber'];
  zip: Location['postalCode'];
  contactTransactions: ProspectContactTransaction[];
};

type ProspectContactTransaction = {
  id: number;
  degree: ContactRequest['degree'];
  category: ContactRequest['category'];
  subject: ContactRequest['subject'];
  cohesionSessionId: ContactRequest['cohesionSessionId'] | null;
  details: Lead;
  trackingContext: ContactRequest['trackingContext'] | null;
};

export type LoadContactResponse = {
  prospectContact: ProspectContact[];
};

export type LoadContactReturn = {
  contact: Omit<ProspectContact, 'contactTransactions'>;
  transaction: ProspectContactTransaction;
};

export type SaveContactParams = {
  degree: ContactRequest['degree'];
  subject: ContactRequest['subject'];
  category: ContactRequest['category'];
  cohesionSessionId: ContactRequest['cohesionSessionId'];
  lead: ContactRequest['details'];
  publisher: string;
};

export type SaveContactApiResponseSuccess = {
  contact: {
    lastTransaction: {
      id: number;
      updatedAt: string;
    }[];
    contactId: string;
  };
};

export type HubApiResponseFailure = {
  code: string;
  error: string;
  path: string;
};

export type EnhancedSchoolData = {
  sdbId: string;
  schoolId: number;
  schoolName: string;
  startDate: string;
  timeToComplete: string;
  tuitionRange: string;
  averageFederalGrantAidAwarded: string;
  acceptanceRate: string;
  graduationRate: string;
};

export type EnhancedSchoolDataResponse = { data?: EnhancedSchoolData[] };

export type SaveContactResult = {
  success: boolean;
  data?: SaveContactApiResponseSuccess['contact'];
  error?: HubApiResponseFailure;
};

export type LoadContactResult = {
  success: boolean;
  data?: LoadContactReturn;
  error?: HubApiResponseFailure;
};

function sendRequest<S, R = unknown>(path: string, method: 'GET' | 'POST' = 'GET', body: R | null = null): Promise<S> {
  const fetchParams: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${import.meta.env.VITE_HUB_API_KEY}`,
    },
    method,
  };
  if (body) {
    fetchParams.body = JSON.stringify(body);
  }
  return fetch(`${import.meta.env.VITE_HUB_API_BASE_URL}${path}`, fetchParams).then((res) => res.json()) as Promise<S>;
}

export async function loadContact(contactId: string, transactionId: number): Promise<LoadContactResult> {
  try {
    const response = await sendRequest<LoadContactResponse & HubApiResponseFailure, ContactRequest>(
      `api/rest/v1/prospect/contact/${contactId}/${transactionId}`
    );

    if (response.error?.length) {
      newRelicNoticeError(nrErrorMap.CONTACT_DB, new Error(response.error), response);
      return { success: false, error: response };
    }

    if (!response.prospectContact.length || !response.prospectContact[0]?.contactTransactions.length) {
      newRelicNoticeError(nrErrorMap.CONTACT_DB, new Error('contactId or transactionId not found or expired'), {
        contactId,
        transactionId,
      });
      return {
        success: false,
        error: {
          error: 'contactId or transactionId not found or expired',
          code: 'NOT_FOUND',
          path: '',
        },
      };
    }
    const { contactTransactions, ...contact } = response.prospectContact[0];
    const transaction = contactTransactions[0];
    newRelicCustomAttribute('contactId', contactId);
    newRelicCustomAttribute('contactTransactionId', transactionId);
    return {
      success: true,
      data: {
        contact,
        transaction,
      },
    };
  } catch (err) {
    newRelicNoticeError(nrErrorMap.CONTACT_DB, new Error((err as HubApiResponseFailure).error));
    return { success: false, error: err as HubApiResponseFailure };
  }
}

export async function saveContact(data: SaveContactParams): Promise<SaveContactResult> {
  const body: ContactRequest = {
    degree: data.degree,
    subject: data.subject,
    category: data.category,
    firstName: data.lead.firstName?.trim(),
    lastName: data.lead.lastName?.trim(),
    email: data.lead.email?.trim(),
    phoneNumber: data.lead.phoneNumber,
    zip: data.lead.location?.postalCode,
    details: data.lead,
    cohesionSessionId: data.cohesionSessionId,
    trackingContext: {
      ...window._Cohesion?.webContext,
      publisher: data.publisher || window.HE?.publisher?.name || window.location.host,
    },
  };

  try {
    const response = await sendRequest<SaveContactApiResponseSuccess & HubApiResponseFailure, ContactRequest>(
      'api/rest/v1/prospect/contact',
      'POST',
      body
    );

    if (response.error?.length) {
      newRelicNoticeError(nrErrorMap.CONTACT_DB, new Error(response.error), response);
      return { success: false, error: response };
    }

    if (!response.contact?.contactId?.length) {
      newRelicNoticeError(nrErrorMap.CONTACT_DB, new Error('invalid contactId returned from contactDb'), {
        contactId: response.contact?.contactId,
      });
      return { success: false, error: response };
    }

    if (!response.contact?.lastTransaction?.length) {
      newRelicNoticeError(nrErrorMap.CONTACT_DB, new Error('invalid transaction returned from contactDb'), {
        contactId: response.contact?.contactId,
      });
      return { success: false, error: response };
    }

    newRelicCustomAttribute('contactId', response.contact?.contactId);
    newRelicCustomAttribute('contactTransactionId', response.contact?.lastTransaction[0]?.id || 0);
    PageActions.ContactSaved({ contactId: response.contact?.contactId });

    return { success: true, data: response.contact };
  } catch (e) {
    newRelicNoticeError(nrErrorMap.CONTACT_DB, new Error((e as HubApiResponseFailure).error));
    return { success: false, error: e as HubApiResponseFailure };
  }
}

export async function fetchEnhancedSchoolData(): Promise<EnhancedSchoolData[]> {
  return sendRequest<EnhancedSchoolDataResponse>('api/rest/v1/school/partner-verified-school-data', 'POST').then(
    (res) => res.data as EnhancedSchoolData[]
  );
}

export type ProgramFormFieldOptions = {
  label: string;
  value: unknown;
};

export type ProgramFormFieldsConfig =
  | {
      [key in BaseUiSchemaKeys]: {
        question: string;
        options: ProgramFormFieldOptions[];
      };
    }
  | null;

export type ProgramFormFieldsConfigResponse = { getProgramFormFieldsConfig: ProgramFormFieldsConfig };

export async function fetchProgramFormFieldConfig(programId: number): Promise<ProgramFormFieldsConfig> {
  const response = await sendRequest<ProgramFormFieldsConfigResponse>(
    'api/rest/v1/program/form-fields-config',
    'POST',
    {
      programId,
    }
  );

  return response?.getProgramFormFieldsConfig as ProgramFormFieldsConfig;
}
